import React, { useContext, useEffect } from 'react';
import cn from 'classnames';

import { LinkButton } from '@vtblife/uikit';

import styles from './header-layout.module.css';
import { MenuItemBase } from '../menu-item-base/menu-item-base';
import { GEO_CONFIRM_HEADER, sendAnalyticsEvent } from '../../analytics';
import { PersonalButtons } from '../personal-buttons/personal-buttons';
import { HeaderContextValue } from '../header-context';
import { CloseButton } from '../close-button/close-button';
import { BurgerButton } from '../burger-button/burger-button';
import { preventBodyScrollOverlayOpened } from '../../utils';
import { useHeaderVariant } from '../hooks';
import { Logo } from '../logo/logo';
import { LogoVariant } from '../logo/images';
import { useAuthorizeContext, useUserRegionContext } from '../../shell';
import { OfferPlacementLink } from '../offer-placement-link/offer-placement-link';
import { getOfferPlacementButtonUrl } from '../utils';
import { HeaderMenuBlock } from '../header-menu-block/header-menu-block';

export interface HeaderLayoutProps {
    className?: string;
    cdnUrl: string;
    logoHref?: string;
    logoVariant: LogoVariant;
    isBordered?: boolean;
    isSticky?: boolean;
    isFullWidth?: boolean;
    isTransparent?: boolean;
    hideOnMobile?: boolean;
    hasBurgerMenu?: boolean;
    personalAreaMobileButtonText?: string | undefined;
    topBanner?: React.ReactNode;
}

/**
 * Хедер на мобиле фиксирован, поэтому под ним идёт спейсер на его высоту.
 * Баннер живёт внутри <header>, а его высоту оба элемента добирают глобальным
 * классом-модификатором (critical CSS / gatsby-party).
 *
 * Слот баннера всегда в дереве (пустой или с контентом). Иначе React 16 при
 * появлении topBanner после useMedia переиспользует div.headerLayoutContent
 * под корень баннера — крестик/CTA въезжают в строку логотипа.
 */
const HeaderFrame: React.FC<{
    topBanner?: React.ReactNode;
    headerClassName?: string;
    headerDataTest?: string;
    children: React.ReactNode;
}> = ({ topBanner, headerClassName, headerDataTest, children }) => {
    const hasTopBanner = Boolean(topBanner);

    return (
        <>
            <header
                className={cn(headerClassName, { headerLayoutWithBanner: hasTopBanner })}
                data-test={headerDataTest}
            >
                <div key="header-top-banner">{topBanner}</div>
                {children}
            </header>
            <div className={cn(styles.headerSpacer, { headerSpacerWithBanner: hasTopBanner })} />
        </>
    );
};

export const HeaderLayout: React.FC<HeaderLayoutProps> = ({
    className,
    logoHref = '/',
    logoVariant,
    isSticky,
    isBordered,
    isFullWidth,
    isTransparent,
    hideOnMobile,
    hasBurgerMenu,
    personalAreaMobileButtonText,
    topBanner,
}) => {
    const {
        isMenuOpened,
        toggleMenu,
        isPersonalButtonsVisible,
        isInfoMenuVisible,
        personalButtons,
        isOnlyLogoVisible,
    } = useContext(HeaderContextValue);
    const { isAuthorized } = useAuthorizeContext();
    const { isShort, isScrolled, iconTransform } = useHeaderVariant(!!isSticky);
    const { userRegionName, showSelectRegionModal } = useUserRegionContext();

    const handleChangeRegionButtonClick = () => {
        showSelectRegionModal?.();
        sendAnalyticsEvent(GEO_CONFIRM_HEADER);
    };

    useEffect(() => {
        preventBodyScrollOverlayOpened(isMenuOpened);
    }, [isMenuOpened]);

    if (isOnlyLogoVisible) {
        return (
            <HeaderFrame topBanner={topBanner} headerClassName={cn(styles.headerLayout, className)}>
                <div key="header-layout-content" className={styles.headerLayoutContent}>
                    <div className={styles.headerLayoutLogo}>
                        <MenuItemBase path={logoHref} url={logoHref} className={styles.headerLayoutLogoIcon}>
                            <Logo variant={logoVariant} transform={iconTransform} />
                        </MenuItemBase>
                    </div>
                </div>
            </HeaderFrame>
        );
    }

    return (
        <HeaderFrame
            topBanner={topBanner}
            headerDataTest={isMenuOpened ? 'header-opened' : 'header-closed'}
            headerClassName={cn(styles.headerLayout, className, {
                [styles.headerLayoutShort]: isShort,
                [styles.headerLayoutSticky]: isSticky && isScrolled,
                [styles.headerLayoutOpened]: isMenuOpened,
                [styles.headerLayoutBordered]: isBordered,
                [styles.headerLayoutTransparent]: isTransparent,
                [styles.headerLayoutHiddenOnMobile]: hideOnMobile,
            })}
        >
            <div
                key="header-layout-content"
                className={cn(styles.headerLayoutContent, {
                    [styles.headerLayoutContentFull]: isFullWidth,
                })}
            >
                <div className={styles.headerLayoutLeft}>
                    <div className={styles.headerLayoutLogo}>
                        <MenuItemBase path={logoHref} url={logoHref} className={styles.headerLayoutLogoIcon}>
                            <Logo variant={logoVariant} transform={iconTransform} />
                        </MenuItemBase>
                    </div>
                    <HeaderMenuBlock
                        isMenuOpened={isMenuOpened}
                        isInfoMenuVisible={isInfoMenuVisible}
                        userRegionName={userRegionName}
                        onChangeRegionClick={handleChangeRegionButtonClick}
                        shouldShowPublicAreaMenuMobile={Boolean(personalAreaMobileButtonText)}
                    />
                </div>
                <div className={styles.headerLayoutPersonalButtonsMobile}>
                    {isPersonalButtonsVisible && (
                        <>
                            <PersonalButtons />
                            <OfferPlacementLink
                                url={getOfferPlacementButtonUrl(isAuthorized, personalButtons?.offerPlacement)}
                            />
                        </>
                    )}
                </div>
                <div className={styles.headerMobileButtonsContainer}>
                    {personalAreaMobileButtonText && (
                        <div className={styles.personalAreaBtn}>
                            <LinkButton variant="secondary-alt-bg" size="xs" href="/personal-area">
                                {personalAreaMobileButtonText}
                            </LinkButton>
                        </div>
                    )}
                    {hasBurgerMenu && (
                        <div className={styles.headerLayoutBurger}>
                            {isMenuOpened ? (
                                <CloseButton onClick={toggleMenu} dataTest="header-close-button" />
                            ) : (
                                <BurgerButton onClick={toggleMenu} dataTest="header-burger-button" />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </HeaderFrame>
    );
};
